import { useState, useRef, useEffect } from 'react';
import { Home, User, LogOut, Menu, X, ChevronDown, LogIn } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../Authcontext.jsx';
import exquraWordmark from '../../assets/branding/exqura-wordmark.png';

const DEFAULT_NAV_ITEMS = [
  {
    type: "dropdown",
    label: "Book Golf",
    href: "",
    items: [
      { label: "About Courses", href: "/about-courses" },
      { label: "Golf Getaways", href: "/golf-getaways" },
      { label: "Exqura Experience", href: "/exqura-experience" },
    ],
  },
  { type: "link", label: "About Us", href: "/AboutUs", items: [] },
  { type: "link", label: "Contact Us", href: "/ContactUs", items: [] },
];

export default function ExquraNavbar({ fields = {} }) {
  const {
    logoImage,
    logoAlt = "Exqura",
    navItems = DEFAULT_NAV_ITEMS,
    signInLabel = "Sign In",
    homeLabel = "Home",
    profileLabel = "Profile",
  } = fields;

  const [mobileOpen, setMobileOpen] = useState(false);
  // Which nav item's dropdown is open, by index — not a single boolean,
  // since navItems is now an admin-reorderable list that could (in
  // principle) hold more than one dropdown-type item.
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const { user } = useAuth();

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/Login');
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-[#0B2421] text-white sticky top-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logoImage || exquraWordmark} alt={logoAlt} className="h-5 sm:h-6 w-auto" />
          </Link>

          {/* Desktop Menu — one ordered list, admin-reorderable via the
              navbar-content CMS block's "Navigation items" field */}
          <nav className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
            {navItems.map((item, i) =>
              item.type === "dropdown" ? (
                <div key={i} className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdown(openDropdown === i ? null : i)
                    }
                    className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {openDropdown === i && (
                    <div className="absolute top-full left-0 mt-3 w-52 bg-[#0B2421] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                      {(item.items || []).map((sub, j) => (
                        <a
                          key={j}
                          href={sub.href}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-3 text-sm text-white/80 hover:text-[#D4AF37] hover:bg-white/5 transition-colors ${j > 0 ? "border-t border-white/5" : ""}`}
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={i}
                  to={item.href}
                  className="text-sm font-medium text-white/80 hover:text-[#D4AF37] transition-colors"
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {!user ? (
              <button onClick={() => navigate('/Login')} className="flex items-center gap-1.5 text-[#D4AF37] border border-[#D4AF37] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#D4AF37]/10 transition-colors">
                <LogIn className="w-4 h-4" />
                {signInLabel}
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-white/80 px-3 py-2 rounded-lg text-sm font-medium hover:text-white hover:bg-white/5 transition-colors">
                  <Home className="w-4 h-4" />
                  {homeLabel}
                </button>
                <button onClick={() => navigate('/Profile')} className="flex items-center gap-1.5 text-white/80 px-3 py-2 rounded-lg text-sm font-medium hover:text-white hover:bg-white/5 transition-colors">
                  <User className="w-4 h-4" />
                  {profileLabel}
                </button>
                <button onClick={handleLogout} className="flex items-center gap-1.5 text-white/60 px-3 py-2 rounded-lg text-sm font-medium hover:text-white hover:bg-white/5 transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 space-y-1">
            {navItems.map((item, i) =>
              item.type === "dropdown" ? (
                <div key={i}>
                  <button
                    onClick={() =>
                      setMobileOpenDropdown(mobileOpenDropdown === i ? null : i)
                    }
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5"
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {mobileOpenDropdown === i && (
                    <div className="ml-4 pl-3 border-l border-white/10 mt-1 space-y-1">
                      {(item.items || []).map((sub, j) => (
                        <a
                          key={j}
                          href={sub.href}
                          className="block py-2 text-sm text-white/70 hover:text-[#D4AF37] transition-colors"
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={i}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5"
                >
                  {item.label}
                </Link>
              ),
            )}

            <div className="flex items-center gap-2 pt-3 flex-wrap">
              {!user ? (
                <button onClick={() => { navigate('/Login'); setMobileOpen(false); }} className="flex items-center gap-1.5 bg-[#D4AF37] text-[#0B2421] px-4 py-2 rounded-lg text-sm font-semibold">
                  <LogIn className="w-4 h-4" /> {signInLabel}
                </button>
              ) : (
                <>
                  <button onClick={() => { navigate('/'); setMobileOpen(false); }} className="flex items-center gap-1.5 text-white px-3 py-2 rounded-lg text-sm font-medium bg-white/5">
                    <Home className="w-4 h-4" /> {homeLabel}
                  </button>
                  <button onClick={() => { navigate('/Profile'); setMobileOpen(false); }} className="flex items-center text-white p-2 rounded-lg bg-white/5">
                    <User className="w-4 h-4" />
                  </button>
                  <button onClick={handleLogout} className="flex items-center text-white/60 p-2 rounded-lg bg-white/5">
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
